import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, BarChart3, Users, ShoppingCart, Monitor, Tablet, Shield } from "lucide-react";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/8f834926-4714-4959-b338-a287e83dcd5c/images/09115f449cc4321ff25180a33470ced8331fabee71ffcd319e7c00cf958ac54e.png";
const HERO_URL = "https://static.prod-images.emergentagent.com/jobs/8f834926-4714-4959-b338-a287e83dcd5c/images/c277bd41f0bf75c2088b9b0a8654150cbb1d095d6995685acda62210d1784ee4.png";

const features = [
  { icon: ShoppingCart, title: "Fast Checkout", desc: "Process orders in seconds with our intuitive POS interface" },
  { icon: BarChart3, title: "Real-time Analytics", desc: "Track sales, inventory, and performance in real-time" },
  { icon: Users, title: "Employee Management", desc: "Manage roles, shifts, and performance effortlessly" },
  { icon: Monitor, title: "Multi-Platform", desc: "Works on iPad, desktop, and any modern browser" },
  { icon: Tablet, title: "Table Management", desc: "Perfect for restaurants with visual table layouts" },
  { icon: Shield, title: "Secure & Reliable", desc: "Enterprise-grade security with role-based access" },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <img src={LOGO_URL} alt="CashUpPOS" className="h-8 w-8" />
            <span className="font-heading text-xl font-bold text-slate-900 tracking-tight">CashUpPOS</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600" data-testid="landing-login-btn">Sign In</Button>
            </Link>
            <Link to="/login">
              <Button className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="landing-get-started-btn">
                Get Started <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-medium mb-4" data-testid="hero-overline">Point of Sale System</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-6" data-testid="hero-title">
              Modern POS for modern businesses
            </h1>
            <p className="text-base sm:text-lg text-slate-500 mb-8 max-w-lg" data-testid="hero-subtitle">
              Powerful, intuitive point-of-sale system built for restaurants and retail. Manage orders, inventory, employees, and analytics all in one place.
            </p>
            <div className="flex gap-4">
              <Link to="/login">
                <Button size="lg" className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white h-12 px-8" data-testid="hero-cta-btn">
                  Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-lg overflow-hidden border border-slate-200 shadow-2xl">
              <img src={HERO_URL} alt="CashUpPOS on iPad" className="w-full" data-testid="hero-image" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)] font-medium mb-3">Features</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              Everything you need to run your business
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div key={i} className="bg-white p-8 border border-slate-200 rounded-lg hover:-translate-y-1 hover:shadow-lg transition-all duration-200" data-testid={`feature-card-${i}`}>
                <f.icon className="h-8 w-8 text-[var(--brand-primary)] mb-4" strokeWidth={1.5} />
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-12">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CashUpPOS" className="h-6 w-6" />
            <span className="text-sm text-slate-500">CashUpPOS</span>
          </div>
          <p className="text-sm text-slate-400">&copy; 2026 CashUpPOS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
